import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Menu from './pages/Menu';
import About from './pages/About';
import Contact from './pages/Contact';
import Bookmarks from './pages/Bookmarks';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import CookiePolicy from './pages/CookiePolicy';
import AccessibilityStatement from './pages/AccessibilityStatement';

import './App.css';

// Component to handle scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Page transition wrapper
function PageWrapper({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

// 404 fallback component
function NotFound() {
  return (
    <PageWrapper>
      <div className="pt-24 pb-12 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center px-8">
          <h1 className="text-4xl font-bold text-luxury-dark mb-4 font-serif">Page Not Found</h1>
          <p className="text-luxury-dark/60 mb-8">The page you're looking for doesn't exist.</p>
          <a
            href="/"
            className="inline-flex items-center bg-luxury-gold text-white px-8 py-3 rounded-sm hover:bg-luxury-dark transition-colors tracking-widest uppercase text-xs font-black"
          >
            Return Home
          </a>
        </div>
      </div>
    </PageWrapper>
  );
}

function App() {
  useEffect(() => {
    // Register service worker with aggressive update check
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered');
            
            // Check for updates on every page load
            registration.update();
            
            // Listen for new service worker
            registration.onupdatefound = () => {
              const installingWorker = registration.installing;
              if (installingWorker) {
                installingWorker.onstatechange = () => {
                  if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // New content available, force reload
                    window.location.reload();
                  }
                };
              }
            };
          })
          .catch((err) => console.log('SW error', err));
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <div className="App" role="main">
          <ScrollToTop />
          <Header />
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={
                <PageWrapper>
                  <Home />
                </PageWrapper>
              } />
              <Route path="/menu" element={
                <PageWrapper>
                  <Menu />
                </PageWrapper>
              } />
              <Route path="/bookmarks" element={
                <PageWrapper>
                  <Bookmarks />
                </PageWrapper>
              } />
              <Route path="/about" element={
                <PageWrapper>
                  <About />
                </PageWrapper>
              } />
              <Route path="/contact" element={
                <PageWrapper>
                  <Contact />
                </PageWrapper>
              } />
              <Route path="/privacy-policy" element={
                <PageWrapper>
                  <PrivacyPolicy />
                </PageWrapper>
              } />
              <Route path="/terms-of-use" element={
                <PageWrapper>
                  <TermsOfUse />
                </PageWrapper>
              } />
              <Route path="/cookie-policy" element={
                <PageWrapper>
                  <CookiePolicy />
                </PageWrapper>
              } />
              <Route path="/accessibility" element={
                <PageWrapper>
                  <AccessibilityStatement />
                </PageWrapper>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AnimatePresence>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;